import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2Icon, PartyPopperIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useBillingServiceGetV1BillingPricesKey } from '@/api/client/queries/common'
import { Button } from '@/components/ui/button'
import { useSubscription } from '@/contexts/subscription-context'
import { useIndividuals } from '@/hooks/useIndividuals'

/**
 * Where Stripe returns after a successful payment.
 *
 * It exists because of a gap: Stripe redirects the moment the money is taken, which is
 * before the webhook has written the entitlement. Returning to an ordinary page in that
 * window would show one that has not changed — identical to a payment that failed. This
 * page holds the gap and says which side of it we are on.
 *
 * It does not poll. `SubscriptionProvider` already listens to the individual's realtime
 * node, and the webhook's write reaches it within a second or two, so the state below
 * flips on its own. Polling would be a second mechanism for something already watched.
 *
 * The session id in the query is deliberately unused. It identifies the purchase, but it
 * is also whatever the browser was last handed — trusting it to grant anything would move
 * the decision off the webhook, which is the only party that has heard from Stripe
 * directly.
 */
const BillingDonePage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { selectedIndividual } = useIndividuals()
  const { isTrial, status } = useSubscription()
  const queryClient = useQueryClient()

  // Anything other than an unpaid state means the webhook has landed. Checked this way
  // round rather than for 'active' specifically, so a subscription that arrives as
  // past_due — paid, but with a renewal already failing — is not reported as still
  // pending forever.
  const isSettled = status === 'active' || (!isTrial && status !== 'expired')

  // What this owner would pay has just changed, and the plans response carries it —
  // `addSeat`, and an `amount` computed for them. The query cache is persisted to
  // IndexedDB, so without this the pre-purchase answer survives the trip out to Stripe and
  // back, and the next individual is quoted the price of the first.
  //
  // Keyed on `isSettled` rather than on mount alone: arriving here only means the money
  // was taken, and the pool the answer is computed from is written by the webhook. Firing
  // once on arrival would often refetch the same stale answer, so this runs again when the
  // webhook actually lands.
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: [useBillingServiceGetV1BillingPricesKey] })
  }, [isSettled, queryClient])

  return (
    <div className="z-10 flex min-h-[70vh] flex-col items-center justify-center gap-6 text-center">
      {isSettled ? (
        <div className="flex size-[52px] items-center justify-center rounded-full bg-[#005c56]">
          <PartyPopperIcon className="size-[26px] text-sidebar-primary" strokeWidth={2.5} />
        </div>
      ) : (
        <Loader2Icon className="size-12 text-brand-funkia motion-safe:animate-spin" />
      )}

      <div className="space-y-2 max-w-lg">
        <h1 className="text-2xl font-heading font-black">
          {isSettled
            ? t('billingDone.title', { name: selectedIndividual?.firstName ?? '' })
            : t('billingDone.pendingTitle')}
        </h1>
        <p className="text-text-secondary text-lg">
          {isSettled ? t('billingDone.description') : t('billingDone.pendingDescription')}
        </p>
      </div>

      {/* Available immediately, not gated on the webhook. Someone who has paid should
          never be stuck on this page because a background write is slow. Outline while
          pending — the primary/viol treatment is reserved for the settled state. */}
      <Button onClick={() => navigate({ to: '/home' })} variant={isSettled ? 'default' : 'outline'}>
        {t('billingDone.continue')}
      </Button>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/billing/done')({
  component: BillingDonePage,
})
